import { forwardRef, memo, useCallback, useMemo, useRef, useState } from "react"
import Icon from "../../Icon"
import Button from "../../Button"
import { useClickOutside } from "../../../hooks/useClickOutside"
import { useTranslation } from "react-i18next"
import { useCalendar } from "../../../hooks/useCalendar"
import Input from ".."
import { DaterangeValue } from "../Daterange"
import { ICON } from "../../Icon/data"
import { DateTime } from "luxon"

type Datepicker = {
  value: string | DaterangeValue
  onChange: (val: string) => void
  dateKey?: string
  format?: string
  placeholder?: string
  size?: string
  label?: string
}

const Datepicker = memo(forwardRef(({
  value: inputValue, onChange, dateKey, format, placeholder, size = 'l', label
}: Datepicker, ref) => {
  const { t } = useTranslation()
  const dropdownRef = useRef()
  const inputRef = useRef()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const value = useMemo(() => typeof inputValue === 'object' ? inputValue[dateKey] : inputValue, [inputValue, dateKey])

  const usedValue = useMemo(() => {
    if (value && DateTime.fromISO(value).isValid) {
      return DateTime.fromISO(value).toISODate()
    } 
    return DateTime.now().toISODate()
  }, [value, t])

  const changeDate = useCallback((date: string) => {
    onChange(date)
    setIsOpen(false)
  }, [onChange])

  const { getMonthCalendar } = useCalendar({ date: usedValue, onChange: (val) => changeDate(val)})

  const chosenFormat = useMemo(() => {
    if (format) return format
    return 'dd.LL.yyyy'
  }, [format])

  return (
    <div className="Datepicker">
      <div className="Datepicker__input">
        <Input
          ref={inputRef}
          type="datesimple"
          size={size}
          label={label}
          value={usedValue}
          format={chosenFormat}
          placeholder={placeholder}
          onInput={(val) => onChange(val)}
        />
        <Button
          type="ghost"
          size="s"
          label={<Icon icon={ICON.CALENDAR} />}
          onClick={() => setIsOpen(true)}
        />
      </div>
      {isOpen && (
        <div ref={dropdownRef} className="Datepicker__dropdown">
          {getMonthCalendar}
        </div>
      )}
    </div>
  )
}))

export default Datepicker
